import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  MultiSelect,
  NumberInput,
  Stack,
  Group,
  Button,
  TextInput,
  Loader,
  Modal,
  Autocomplete,
} from '@mantine/core';
import { useMentorSearchStore } from '@/stores/useMentorSearchStore';
import debounce from 'lodash.debounce';

type ExpertiseOption = { value: string; label: string };
type LocationOption = { value: string; label: string };

export default function AdvancedFiltersModal() {
  const modalOpened = useMentorSearchStore((s) => s.modalOpened);
  const closeModal = useMentorSearchStore((s) => s.closeModal);
  const modalValues = useMentorSearchStore((s) => s.modalValues);
  const setModalValues = useMentorSearchStore((s) => s.setModalValues);
  const setFilters = useMentorSearchStore((s) => s.setFilters);
  const setCursor = useMentorSearchStore((s) => s.setCursor);
  const fetchMentors = useMentorSearchStore((s) => s.fetchMentors);

  const [expertiseOptions, setExpertiseOptions] = useState<ExpertiseOption[]>([]);
  const [expertiseLoading, setExpertiseLoading] = useState(false);
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [locationLoading, setLocationLoading] = useState(false);
  const isMountedRef = useRef(true);

  // Optional local state for expertise search input
  const [searchExpertise, setSearchExpertise] = useState('');

  useEffect(() => {
    isMountedRef.current = true;
    
    setExpertiseLoading(true);
    fetch('/api/expertises')
      .then((res) => res.json())
      .then((data: { id: number; name: string }[]) => {
        if (isMountedRef.current) {
          setExpertiseOptions(data.map((e) => ({ value: e.name, label: e.name })));
        }
      })
      .catch(() => {
        if (isMountedRef.current) {
          setExpertiseOptions([]);
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setExpertiseLoading(false);
        }
      });
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchLocations = useCallback(
    debounce(async (query: string) => {
      if (!query) {
        if (isMountedRef.current) {
          setLocationOptions([]);
        }
        return;
      }
      if (isMountedRef.current) {
        setLocationLoading(true);
      }
      try {
        const res = await fetch(`/api/location-search?q=${encodeURIComponent(query)}`);
        const data = await res.json();
        if (isMountedRef.current) {
          setLocationOptions(data);
        }
      } catch {
        if (isMountedRef.current) {
          setLocationOptions([]);
        }
      } finally {
        if (isMountedRef.current) {
          setLocationLoading(false);
        }
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchLocations(modalValues.location || '');
    
    // Cleanup: cancel any pending debounced calls when component unmounts
    return () => {
      fetchLocations.cancel();
    };
  }, [modalValues.location, fetchLocations]);

  const isDistanceEnabled = Boolean(modalValues.location?.trim());

  const handleField = <K extends keyof typeof modalValues>(key: K, value: typeof modalValues[K]) =>
    setModalValues({ ...modalValues, [key]: value });

  // Expertise is now an array
  const handleExpertiseChange = (value: string[]) => {
    handleField('expertise', value);
  };

  const handleLocationChange = (value: string) => {
    handleField('location', value);
  };

  const handleApply = () => {
    const cleanedFilters: Partial<typeof modalValues> = {};

    if (modalValues.expertise && modalValues.expertise.length > 0) {
      cleanedFilters.expertise = modalValues.expertise.join(',');
    }
    if (modalValues.location?.trim()) cleanedFilters.location = modalValues.location.trim();
    if (modalValues.name?.trim()) cleanedFilters.name = modalValues.name.trim();
    if (modalValues.bio?.trim()) cleanedFilters.bio = modalValues.bio.trim();
    if (isDistanceEnabled && modalValues.distance && modalValues.distance > 0) {
      cleanedFilters.distance = modalValues.distance;
    }
    setFilters(cleanedFilters);
    setCursor(null);
    fetchMentors(true, cleanedFilters);
    closeModal();
  };

  return (
    <Modal opened={modalOpened} onClose={closeModal} title="Advanced filters" centered data-testid="modal" size="lg">
      <Stack>
        <MultiSelect
          label="Skills"
          placeholder="Select one or more skills"
          data={expertiseOptions}
          value={modalValues.expertise || []}
          onChange={handleExpertiseChange}
          searchable
          rightSection={expertiseLoading ? <Loader size="xs" /> : null}
          data-testid="expertise"
        />

        <Autocomplete
          label="Location"
          placeholder="Start typing to search location"
          data={locationOptions}
          value={modalValues.location || ''}
          onChange={handleLocationChange}
          rightSection={locationLoading ? <Loader size="xs" /> : null}
          data-testid="location"
        />

        <NumberInput
          label="Distance (km)"
          placeholder="Only applies if location is set"
          value={modalValues.distance}
          onChange={(val) => handleField('distance', val ?? undefined)}
          data-testid="distance"
          disabled={!isDistanceEnabled}
          min={1}
          max={500}
          step={1}
          description={!isDistanceEnabled ? 'Set a location first to enable distance' : undefined}
        />

        <TextInput
          label="Mentor Name"
          value={modalValues.name || ''}
          onChange={(e) => handleField('name', e.currentTarget.value)}
          data-testid="name"
          placeholder="Search by mentor name"
        />

        <TextInput
          label="Bio Keywords"
          value={modalValues.bio || ''}
          onChange={(e) => handleField('bio', e.currentTarget.value)}
          data-testid="bio"
          placeholder="Keywords from mentor bio"
        />

        <Group justify="flex-end" mt="md">
          <Button variant="subtle" onClick={closeModal} data-testid="modal-close">
            Cancel
          </Button>
          <Button onClick={handleApply} data-testid="apply-filters">
            Apply filters
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
